import { type JSX, useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Badge,
  Button,
  Code,
  Divider,
  Group,
  Loader,
  Modal,
  MultiSelect,
  NumberInput,
  Paper,
  Progress,
  ScrollArea,
  SegmentedControl,
  Stack,
  Table,
  Text,
  TextInput,
  Textarea,
  Tooltip,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import {
  type Capacidades,
  type ContextoDeEnvios,
  type EnvioDetalhe,
  type EnvioNaLista,
  type EstadoDestinatario,
  type ListaDeEnvios,
  type ListaMarcaveis,
  type PreviaDeEnvio,
  type TagNaLista,
  type ResultadoEdicaoDeEnvio,
  type TipoDeEnvio,
  type VariavelDeEnvio,
  COR_ESTADO_ENVIO,
  ROTULO_DESTINATARIO,
  ROTULO_ESTADO_ENVIO,
  VARIAVEIS_DE_ENVIO,
  api,
  explicarEnvio,
  explicarMotivoDestinatario,
} from '../lib/api.js';
import { BarraDeFiltros, BlocoDeAjuda, FaixaDeNumeros, Numero, Secao, Vazio } from './ui.js';
import { ConfiguracaoEnvios } from './ConfiguracaoEnvios.js';

/**
 * Envios: avisos de serviço e campanhas.
 *
 * A TELA INTEIRA É ORGANIZADA EM VOLTA DE UM RISCO. O número do disparo é hoje
 * o mesmo do bot de atendimento, e um bloqueio do WhatsApp não derruba a
 * campanha — derruba o atendimento. Por isso:
 *
 *  - o aviso de número compartilhado abre a tela, e some sozinho no dia em que
 *    o ambiente apontar para uma instância dedicada;
 *  - o número final e as exclusões aparecem ANTES de o envio existir;
 *  - o envio de teste é obrigatório, e o botão de iniciar fica travado até ele
 *    sair;
 *  - a janela de horário e o teto diário são mostrados, não escondidos: quem
 *    dispara às 19h50 precisa saber que a lista vai parar em dez minutos.
 */

function data(iso: string | null): string {
  return iso ? new Date(iso).toLocaleString('pt-BR') : '—';
}

/** Estados em que a pessoa ainda pode agir no envio. */
function podeIniciar(e: EnvioNaLista): boolean {
  return (e.estado === 'rascunho' || e.estado === 'agendado') && e.testeEm !== null;
}

export function Envios({ capacidades }: { capacidades: Capacidades }): JSX.Element {
  const [dados, setDados] = useState<ListaDeEnvios | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [criando, setCriando] = useState(false);
  const [aberto, setAberto] = useState<string | null>(null);
  const [configurando, setConfigurando] = useState(false);

  const carregar = useCallback(async () => {
    try {
      setDados(await api.get<ListaDeEnvios>('/admin/envios'));
      setErro(null);
    } catch (e) {
      setErro(explicarEnvio(e, 'falha ao carregar os envios'));
    }
  }, []);

  useEffect(() => {
    void carregar();
  }, [carregar]);

  /**
   * Recarrega sozinho enquanto há envio em curso.
   *
   * Só enquanto HÁ: um temporizador permanente martelaria o servidor com a
   * tela aberta no balcão o dia inteiro, e fora do disparo nada muda sozinho.
   */
  const emCurso = (dados?.itens ?? []).some(
    (e) => e.estado === 'enviando' || e.estado === 'agendado',
  );
  useEffect(() => {
    if (!emCurso) return;
    const timer = setInterval(() => void carregar(), 15_000);
    return () => clearInterval(timer);
  }, [emCurso, carregar]);

  const contexto = dados?.contexto ?? null;

  return (
    <Stack gap="md">
      {contexto && <AvisosDoAmbiente contexto={contexto} />}

      {erro && (
        <Alert color="red" variant="light" role="alert">
          <Group gap="sm">
            <Text size="sm">{erro}</Text>
            <Button size="xs" onClick={() => void carregar()}>
              Tentar de novo
            </Button>
          </Group>
        </Alert>
      )}

      {contexto && (
        <FaixaDeNumeros>
          <Numero
            rotulo="enviadas hoje"
            valor={`${contexto.teto.usadasHoje}/${contexto.teto.teto}`}
            nota={
              contexto.teto.atingido
                ? 'teto do dia atingido: retoma amanhã'
                : `cabem ${contexto.teto.restante} hoje`
            }
            {...(contexto.teto.atingido ? { cor: 'orange' } : {})}
          />
          <Numero
            rotulo="janela de disparo"
            valor={`${contexto.limites.janelaInicio}h–${contexto.limites.janelaFim}h`}
            nota={
              contexto.janelaFechadaAte
                ? `fechada · abre ${contexto.janelaFechadaAte}`
                : 'aberta agora · seg a sáb'
            }
            {...(contexto.janelaFechadaAte ? { cor: 'orange' } : {})}
          />
          <Numero
            rotulo="pausa entre mensagens"
            valor={`${contexto.limites.pausaMinSeg}–${contexto.limites.pausaMaxSeg}s`}
            nota="aleatória, por padrão"
          />
          <Numero
            rotulo="número do disparo"
            valor={contexto.instanciaPadrao}
            nota={
              contexto.numeroCompartilhado
                ? 'o MESMO do atendimento'
                : 'instância dedicada'
            }
          />
        </FaixaDeNumeros>
      )}

      <Secao
        numero="01"
        titulo="Envios"
        subtitulo="um por vez, com pausa aleatória e teto diário"
        acao={
          capacidades.disparar ? (
            <Group gap="xs">
              {/* A ENTRADA só existe para admin: editor e leitor não a veem,
                  e a rota também só responde a admin. Esconder sem fechar a
                  rota seria decoração. */}
              {capacidades.administrar && (
                <Button size="xs" variant="default" onClick={() => setConfigurando(true)}>
                  Configuração
                </Button>
              )}
              <Button size="xs" onClick={() => setCriando(true)}>
                Novo envio
              </Button>
            </Group>
          ) : undefined
        }
      >
        {!dados && !erro && (
          <Group gap="xs">
            <Loader size="sm" />
            <Text c="dimmed" size="sm">
              Carregando…
            </Text>
          </Group>
        )}

        {dados && dados.itens.length === 0 && (
          <Vazio titulo="Nenhum envio ainda.">
            Um envio nasce como rascunho: você escolhe quem recebe (por tipo de pessoa, por tag, ou
            os dois), escreve o texto, confere o número final e as exclusões, manda um{' '}
            <strong>envio de teste</strong> para o seu celular e só então inicia. Nada sai antes
            disso.
          </Vazio>
        )}

        {dados && dados.itens.length > 0 && (
          <Table.ScrollContainer minWidth={760}>
            <Table highlightOnHover verticalSpacing="xs" fz="sm">
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Envio</Table.Th>
                  <Table.Th>Estado</Table.Th>
                  <Table.Th>Progresso</Table.Th>
                  <Table.Th ta="right">Enviados</Table.Th>
                  <Table.Th ta="right">Entregues</Table.Th>
                  <Table.Th ta="right">Falhas</Table.Th>
                  <Table.Th>Criado</Table.Th>
                  <Table.Th />
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {dados.itens.map((e) => (
                  <Table.Tr key={e.id}>
                    <Table.Td>
                      <Stack gap={0}>
                        <Text size="sm" fw={500}>
                          {e.nome}
                        </Text>
                        <Group gap={4}>
                          <Badge size="xs" variant="light">
                            {e.tipo === 'campanha' ? 'campanha' : 'aviso'}
                          </Badge>
                          {e.temImagem && (
                            <Badge size="xs" variant="light" color="gray">
                              imagem
                            </Badge>
                          )}
                          {e.testeEm === null && (
                            <Tooltip label="Iniciar fica travado até um envio de teste sair">
                              <Badge size="xs" variant="light" color="orange">
                                sem teste
                              </Badge>
                            </Tooltip>
                          )}
                        </Group>
                      </Stack>
                    </Table.Td>
                    <Table.Td>
                      <Badge size="sm" variant="light" color={COR_ESTADO_ENVIO[e.estado]}>
                        {ROTULO_ESTADO_ENVIO[e.estado]}
                      </Badge>
                    </Table.Td>
                    <Table.Td>
                      <Stack gap={2} w={120}>
                        <Progress
                          value={(e.contagens.progresso ?? 0) * 100}
                          size="sm"
                          color={e.estado === 'enviando' ? 'teal' : 'gray'}
                        />
                        <Text size="xs" c="dimmed">
                          {e.contagens.total - e.contagens.pendentes}/{e.contagens.total}
                        </Text>
                      </Stack>
                    </Table.Td>
                    <Table.Td ta="right">
                      <Text ff="monospace" size="sm">
                        {e.contagens.enviados}
                      </Text>
                    </Table.Td>
                    <Table.Td ta="right">
                      <Text ff="monospace" size="sm" c="dimmed">
                        {e.contagens.entregues}
                      </Text>
                    </Table.Td>
                    <Table.Td ta="right">
                      <Text
                        ff="monospace"
                        size="sm"
                        fw={e.contagens.falhas > 0 ? 700 : 400}
                        c={e.contagens.falhas > 0 ? 'red' : 'dimmed'}
                      >
                        {e.contagens.falhas}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Stack gap={0}>
                        <Text size="xs" c="dimmed">
                          {data(e.criadoEm)}
                        </Text>
                        {e.criadoPorNome && (
                          <Text size="xs" c="dimmed">
                            por {e.criadoPorNome}
                          </Text>
                        )}
                      </Stack>
                    </Table.Td>
                    <Table.Td>
                      <Button size="xs" variant="subtle" onClick={() => setAberto(e.id)}>
                        Abrir
                      </Button>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>
        )}

        <BlocoDeAjuda
          secoes={[
            {
              id: 'como',
              titulo: 'Como um envio funciona, e por que ele é lento de propósito',
              corpo: (
                <>
                  <Text size="sm">
                    <strong>Um envio por vez em todo o sistema.</strong> Dois disparos ao mesmo
                    tempo dobrariam a velocidade de saída do número, e é a velocidade que faz uma
                    conta de WhatsApp parecer um robô. Se você iniciar um segundo envio, ele espera
                    o primeiro terminar.
                  </Text>
                  <Text size="sm">
                    <strong>Entre duas mensagens há uma pausa de alguns segundos</strong>, sorteada
                    dentro da faixa mostrada acima. Uma lista de 100 pessoas leva cerca de uma hora.
                    Isso é o recurso funcionando, não travado.
                  </Text>
                  <Text size="sm">
                    <strong>Fora da janela o envio para e retoma sozinho</strong> na próxima
                    abertura. O mesmo vale para o teto diário: batido o teto, ele continua no dia
                    seguinte, do ponto em que parou. Nenhum dos dois perde destinatário.
                  </Text>
                  <Text size="sm">
                    <strong>Quem está em atendimento com a secretaria é adiado</strong>, não
                    atropelado: a mensagem não cai no meio de uma conversa em curso. Ele é
                    retentado sozinho a cada poucos minutos. Se a conversa ficar aberta por mais de
                    um dia de disparo, ele passa a <em>falhou</em> com o motivo explicado na tabela
                    — e aí o envio pode fechar, em vez de ficar preso esperando. Ao{' '}
                    <em>retomar</em> um envio pausado, esse prazo recomeça.
                  </Text>
                  <Text size="sm">
                    <strong>O envio de teste confirma a combinação inteira:</strong> este número,
                    este texto, esta lista. Mudar o texto, trocar a imagem ou{' '}
                    <em>redefinir os destinatários</em> derruba o teste, e o botão de iniciar volta
                    a ficar travado. Não é burocracia: testar com uma turma e disparar para outra
                    não testou nada do que vai sair.
                  </Text>
                  <Text size="sm">
                    <strong>Aviso (serviço) e campanha são coisas diferentes.</strong> A campanha
                    recebe automaticamente o rodapé “Para não receber mais, responda SAIR”; o aviso
                    de serviço não. Avisar a turma que a aula mudou de horário com um pedido de
                    descadastro no pé seria convidar o aluno a sair de uma lista que ele precisa
                    receber.
                  </Text>
                  <Text size="sm" c="dimmed">
                    Quem responde SAIR, PARAR, CANCELAR ou DESCADASTRAR é marcado na hora e sai de{' '}
                    <strong>todas</strong> as listas, aviso de serviço inclusive.
                  </Text>
                </>
              ),
            },
          ]}
        />
      </Secao>

      {criando && contexto && (
        <NovoEnvio
          contexto={contexto}
          aoFechar={() => setCriando(false)}
          aoCriar={(id) => {
            setCriando(false);
            setAberto(id);
            void carregar();
          }}
        />
      )}

      {capacidades.administrar && (
        <ConfiguracaoEnvios
          aberto={configurando}
          aoFechar={() => setConfigurando(false)}
          // A chave geral e os limites aparecem na faixa de números desta
          // seção: sem recarregar, a tela mostraria o estado de antes.
          aoMudar={() => void carregar()}
        />
      )}

      {aberto && (
        <DetalheDoEnvio
          envioId={aberto}
          capacidades={capacidades}
          aoFechar={() => {
            setAberto(null);
            void carregar();
          }}
          aoMudar={() => void carregar()}
        />
      )}
    </Stack>
  );
}

// ---------------------------------------------------------------------------

/**
 * O aviso de MODO SOMBRA.
 *
 * `BOT_MODE=shadow` cala o bot de atendimento — e não tem efeito nenhum sobre
 * o envio em massa. Quem está com o sistema em sombra naturalmente supõe que
 * nada sai para fora, e essa suposição errada custaria um disparo real para
 * centenas de pessoas achando que era simulação.
 *
 * Aparece em TRÊS lugares (topo da seção, tela de criação, confirmação de
 * disparo) e é um componente só, para as três frases não divergirem.
 * `variant="filled"` na confirmação: ali é a última tela antes de a mensagem
 * sair, e o aviso tem de competir com o botão.
 */
export function AvisoDeSombra({
  modoDoBot,
  destaque = false,
}: {
  modoDoBot: string;
  destaque?: boolean;
}): JSX.Element | null {
  if (modoDoBot !== 'shadow') return null;
  return (
    <Alert
      color="red"
      variant={destaque ? 'filled' : 'light'}
      title="O bot está em modo sombra — mas o envio sai de verdade"
      role="alert"
    >
      <Text size="sm">
        <Code>BOT_MODE=shadow</Code> cala o bot no <strong>atendimento</strong>: ele processa a
        mensagem do aluno e não responde. Isso <strong>não vale para esta tela</strong>. Um envio
        iniciado aqui chega de verdade no WhatsApp de cada destinatário — não há simulação, não há
        ensaio.
      </Text>
    </Alert>
  );
}

/**
 * Os avisos que a tela precisa dar antes de qualquer número.
 *
 * O de número compartilhado SOME sozinho quando o ambiente aponta para uma
 * instância dedicada — é a mesma informação que `compartilhaNumeroComAtendimento`
 * calcula no servidor, e não um texto fixo que alguém teria de lembrar de
 * apagar.
 */
function AvisosDoAmbiente({ contexto }: { contexto: ContextoDeEnvios }): JSX.Element {
  return (
    <Stack gap="xs">
      {/* O de sombra vem PRIMEIRO: é o que contradiz uma suposição, e os
          outros dois são sobre risco e configuração. */}
      <AvisoDeSombra modoDoBot={contexto.modoDoBot} />

      {!contexto.ativo && (
        <Alert color="orange" variant="light" title="Disparo desligado neste ambiente">
          <Text size="sm">
            Dá para montar e revisar envios, mas <strong>iniciar é recusado</strong>. Ligar é
            decisão explícita: <Code>ENVIO_ATIVO=true</Code> nos serviços <Code>api</Code> e{' '}
            <Code>worker</Code>, e reiniciar os dois.
          </Text>
        </Alert>
      )}

      {contexto.numeroCompartilhado && (
        <Alert color="yellow" variant="light" title="O disparo sai do mesmo número do atendimento">
          <Text size="sm">
            Um bloqueio do WhatsApp aqui não derruba só a campanha —{' '}
            <strong>derruba o atendimento inteiro</strong>. Por isso o teto diário, a janela de
            horário e a pausa entre mensagens são conservadores. Para separar os dois, aponte{' '}
            <Code>ENVIO_INSTANCIA</Code> para uma instância dedicada; este aviso desaparece
            sozinho quando isso acontecer.
          </Text>
        </Alert>
      )}
    </Stack>
  );
}

// ---------------------------------------------------------------------------

const TIPOS_DE_ENVIO: { value: TipoDeEnvio; label: string }[] = [
  { value: 'servico', label: 'Aviso (serviço)' },
  { value: 'campanha', label: 'Campanha' },
];

/**
 * Criação de envio.
 *
 * A PRÉVIA É O CENTRO DA TELA, não um detalhe no pé: "mandar para a turma" soa
 * como 40 pessoas e a lista real costuma dar 18, porque metade não tem celular
 * utilizável no cadastro. Descobrir isso depois do disparo custa uma
 * reclamação de aluno que não foi avisado.
 */
function NovoEnvio({
  contexto,
  aoFechar,
  aoCriar,
}: {
  contexto: ContextoDeEnvios;
  aoFechar: () => void;
  aoCriar: (id: string) => void;
}): JSX.Element {
  const [nome, setNome] = useState('');
  const [tipo, setTipo] = useState<TipoDeEnvio>('servico');
  const [tipos, setTipos] = useState<string[]>([]);
  const [tags, setTags] = useState<string[]>([]);
  const [texto, setTexto] = useState('');
  const [inicioEm, setInicioEm] = useState('');
  const [pausaMin, setPausaMin] = useState<number>(contexto.limites.pausaMinSeg);
  const [pausaMax, setPausaMax] = useState<number>(contexto.limites.pausaMaxSeg);

  const [opcoesDeTipo, setOpcoesDeTipo] = useState<{ value: string; label: string }[]>([]);
  const [opcoesDeTag, setOpcoesDeTag] = useState<{ value: string; label: string }[]>([]);

  const [previa, setPrevia] = useState<PreviaDeEnvio | null>(null);
  const [conferindo, setConferindo] = useState(false);
  const [excluirSemVariavel, setExcluirSemVariavel] = useState(false);
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  const areaRef = useRef<HTMLTextAreaElement>(null);

  // As duas listas de critério, numa chamada cada. `contatos-marcaveis` já
  // devolve os tipos com contagem, que é o que o seletor precisa dizer.
  useEffect(() => {
    void api
      .get<ListaMarcaveis>('/admin/contatos-marcaveis?limite=1')
      .then((r) =>
        setOpcoesDeTipo(
          r.tipos.map((t) => ({ value: t.tipo, label: `${t.tipo} (${t.total})` })),
        ),
      )
      .catch(() => undefined);
    void api
      .get<{ itens: TagNaLista[] }>('/admin/tags')
      .then((r) =>
        setOpcoesDeTag(
          r.itens.map((t) => ({
            value: t.id,
            label: `${t.nome} — ${t.alcancaveis} receberiam`,
          })),
        ),
      )
      .catch(() => undefined);
  }, []);

  const temCriterio = tipos.length > 0 || tags.length > 0;

  /**
   * A prévia é refeita a cada mudança de critério ou de texto, com um atraso.
   *
   * Sem o atraso, cada tecla digitada no texto viraria uma varredura da base de
   * contatos. 400 ms é curto o bastante para a tela parecer viva.
   */
  const conferir = useCallback(async () => {
    if (!temCriterio || !texto.trim()) {
      setPrevia(null);
      return;
    }
    setConferindo(true);
    try {
      setPrevia(
        await api.post<PreviaDeEnvio>('/admin/envios/previa', {
          criterios: { tipos, tags },
          texto,
          tipo,
        }),
      );
      setErro(null);
    } catch (e) {
      setPrevia(null);
      setErro(explicarEnvio(e, 'falha ao conferir a lista'));
    } finally {
      setConferindo(false);
    }
  }, [temCriterio, tipos, tags, texto, tipo]);

  useEffect(() => {
    const atraso = setTimeout(() => void conferir(), 400);
    return () => clearTimeout(atraso);
  }, [conferir]);

  /** Insere a variável onde o cursor está, não no fim do texto. */
  function inserir(variavel: VariavelDeEnvio): void {
    const area = areaRef.current;
    const marca = `{${variavel}}`;
    if (!area) {
      setTexto((t) => t + marca);
      return;
    }
    const inicio = area.selectionStart;
    const fim = area.selectionEnd;
    setTexto((t) => t.slice(0, inicio) + marca + t.slice(fim));
    // Devolve o foco com o cursor depois da marca inserida.
    requestAnimationFrame(() => {
      area.focus();
      area.setSelectionRange(inicio + marca.length, inicio + marca.length);
    });
  }

  async function criar(): Promise<void> {
    setSalvando(true);
    try {
      const r = await api.post<{ id: string; total: number }>('/admin/envios', {
        nome,
        tipo,
        texto,
        criterios: { tipos, tags },
        ...(inicioEm ? { inicioEm: new Date(inicioEm).toISOString() } : {}),
        pausaMinSeg: pausaMin,
        pausaMaxSeg: pausaMax,
        ...(excluirSemVariavel ? { excluirSemVariavel: true } : {}),
      });
      notifications.show({
        title: 'Rascunho criado',
        message: `${r.total} destinatários. Mande um envio de teste antes de iniciar.`,
        color: 'green',
      });
      aoCriar(r.id);
    } catch (e) {
      setErro(explicarEnvio(e, 'falha ao criar o envio'));
    } finally {
      setSalvando(false);
    }
  }

  const buracos = previa?.semVariavel.length ?? 0;
  const textoOk = previa?.texto.ok ?? false;
  const prontoParaCriar =
    nome.trim() !== '' &&
    texto.trim() !== '' &&
    temCriterio &&
    textoOk &&
    (previa?.total ?? 0) > 0 &&
    (buracos === 0 || excluirSemVariavel);

  return (
    <Modal opened onClose={aoFechar} title="Novo envio" size="xl" centered>
      <Stack gap="md">
        {/* Primeira coisa do formulário, antes de qualquer campo: quem está em
            sombra tem de ler isto ANTES de montar a lista, não depois. */}
        <AvisoDeSombra modoDoBot={contexto.modoDoBot} />

        <Group grow align="flex-start">
          <TextInput
            label="Nome do envio"
            description="Só para você reconhecer na lista. Não vai na mensagem."
            value={nome}
            onChange={(e) => setNome(e.currentTarget.value)}
            placeholder="Aviso de mudança de horário — turma de janeiro"
          />
          <Stack gap={4}>
            <Text size="sm" fw={500}>
              Tipo
            </Text>
            <SegmentedControl
              value={tipo}
              onChange={(v) => setTipo(v as TipoDeEnvio)}
              data={TIPOS_DE_ENVIO}
              size="xs"
            />
            <Text size="xs" c="dimmed">
              {tipo === 'campanha'
                ? `Recebe o rodapé automático: “${contexto.rodapeCampanha}”`
                : 'Aviso transacional: sem rodapé de descadastro.'}
            </Text>
          </Stack>
        </Group>

        <Divider label="Quem recebe" labelPosition="left" />

        <Group grow align="flex-start">
          <MultiSelect
            label="Tipo de pessoa"
            description="Vale qualquer um dos escolhidos."
            data={opcoesDeTipo}
            value={tipos}
            onChange={setTipos}
            searchable
            clearable
          />
          <MultiSelect
            label="Tags"
            description="Vale qualquer uma das escolhidas."
            data={opcoesDeTag}
            value={tags}
            onChange={setTags}
            searchable
            clearable
          />
        </Group>

        {tipos.length > 0 && tags.length > 0 && (
          <Text size="xs" c="dimmed">
            Com os dois preenchidos, a lista é a <strong>interseção</strong>: quem é de um dos
            tipos <em>e</em> tem uma das tags.
          </Text>
        )}

        <Divider label="A mensagem" labelPosition="left" />

        <Stack gap={6}>
          <Group gap={4}>
            <Text size="xs" c="dimmed" mr={4}>
              Inserir:
            </Text>
            {VARIAVEIS_DE_ENVIO.map((v) => (
              <Button key={v} size="compact-xs" variant="default" onClick={() => inserir(v)}>
                {`{${v}}`}
              </Button>
            ))}
          </Group>
          <Textarea
            ref={areaRef}
            label="Texto"
            description="CPF, e-mail e data de nascimento não podem sair em mensagem — a validação recusa."
            autosize
            minRows={4}
            maxRows={12}
            value={texto}
            onChange={(e) => setTexto(e.currentTarget.value)}
            placeholder={'Olá, {nome}! A aula de amanhã mudou para 14h.'}
          />
        </Stack>

        {previa && previa.texto.problemas.length > 0 && (
          <Alert color="red" variant="light" title="O texto não pode ser enviado assim">
            <Stack gap={2}>
              {previa.texto.problemas.map((p) => (
                <Text key={p.marcador} size="sm">
                  <Code>{`{${p.marcador}}`}</Code> {p.mensagem}
                </Text>
              ))}
            </Stack>
          </Alert>
        )}

        <Divider label="Quando" labelPosition="left" />

        <Group grow align="flex-start">
          <TextInput
            label="Agendar para"
            description="Vazio = começa assim que você iniciar."
            type="datetime-local"
            value={inicioEm}
            onChange={(e) => setInicioEm(e.currentTarget.value)}
          />
          <NumberInput
            label="Pausa mínima (s)"
            min={0}
            max={3600}
            value={pausaMin}
            onChange={(v) => setPausaMin(Number(v) || 0)}
          />
          <NumberInput
            label="Pausa máxima (s)"
            min={0}
            max={3600}
            value={pausaMax}
            onChange={(v) => setPausaMax(Number(v) || 0)}
          />
        </Group>

        <Divider label="O que vai acontecer" labelPosition="left" />

        <PreviaDaLista
          previa={previa}
          conferindo={conferindo}
          temCriterio={temCriterio}
          temTexto={texto.trim() !== ''}
          excluirSemVariavel={excluirSemVariavel}
          aoAlternarExclusao={setExcluirSemVariavel}
        />

        {erro && (
          <Alert color="red" variant="light" role="alert">
            <Text size="sm">{erro}</Text>
          </Alert>
        )}

        <Group justify="flex-end">
          <Button variant="default" onClick={aoFechar}>
            Cancelar
          </Button>
          <Button loading={salvando} disabled={!prontoParaCriar} onClick={() => void criar()}>
            Criar rascunho
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

// ---------------------------------------------------------------------------

function PreviaDaLista({
  previa,
  conferindo,
  temCriterio,
  temTexto,
  excluirSemVariavel,
  aoAlternarExclusao,
}: {
  previa: PreviaDeEnvio | null;
  conferindo: boolean;
  temCriterio: boolean;
  temTexto: boolean;
  excluirSemVariavel: boolean;
  aoAlternarExclusao: (valor: boolean) => void;
}): JSX.Element {
  if (!temCriterio || !temTexto) {
    return (
      <Vazio titulo="Escolha quem recebe e escreva o texto.">
        O número final e a lista de quem fica de fora aparecem aqui, com o motivo de cada exclusão
        — antes de o envio existir.
      </Vazio>
    );
  }

  if (!previa) {
    return (
      <Group gap="xs">
        <Loader size="sm" />
        <Text size="sm" c="dimmed">
          Montando a lista…
        </Text>
      </Group>
    );
  }

  return (
    <Stack gap="sm">
      <Paper withBorder p="md" radius="md">
        <Group justify="space-between" align="flex-start" wrap="nowrap">
          <Stack gap={0}>
            <Group gap="xs" align="baseline">
              <Text fz={36} fw={700} lh={1} ff="monospace">
                {previa.total}
              </Text>
              <Text size="sm" c="dimmed">
                {previa.total === 1 ? 'pessoa recebe' : 'pessoas recebem'}
              </Text>
              {conferindo && <Loader size="xs" />}
            </Group>
            <Text size="xs" c="dimmed">
              de {previa.candidatos} que casam os critérios
            </Text>
          </Stack>

          {previa.total > previa.contexto.teto.restante && (
            <Tooltip
              label="O envio não é cancelado: ele para ao bater o teto e continua amanhã, do ponto em que parou."
              multiline
              w={260}
            >
              <Badge color="orange" variant="light">
                passa do teto de hoje
              </Badge>
            </Tooltip>
          )}
        </Group>
      </Paper>

      {previa.exclusoes.length > 0 && (
        <Paper withBorder p="sm" radius="md">
          <Stack gap="xs">
            <Text size="sm" fw={600}>
              Quem fica de fora
            </Text>
            {previa.exclusoes.map((ex) => (
              <Stack key={ex.motivo} gap={0}>
                <Group gap="xs">
                  <Text ff="monospace" size="sm" fw={600}>
                    {ex.total}
                  </Text>
                  <Text size="sm">{ex.rotulo}</Text>
                </Group>
                <Text size="xs" c="dimmed">
                  {ex.exemplos.map((e) => e.nome ?? e.telefoneMascarado ?? '—').join(', ')}
                  {ex.total > ex.exemplos.length ? ` e mais ${ex.total - ex.exemplos.length}` : ''}
                </Text>
              </Stack>
            ))}
            <Text size="xs" c="dimmed">
              Cada um desses vira uma linha no detalhe do envio, com o motivo — a pergunta que
              aparece depois é “por que a Ana não recebeu”, não “quantos não receberam”.
            </Text>
          </Stack>
        </Paper>
      )}

      {previa.semVariavel.length > 0 && (
        <Alert color="orange" variant="light" title="Há destinatários sem valor para uma variável">
          <Stack gap="xs">
            <Text size="sm">
              {previa.semVariavel.length}{' '}
              {previa.semVariavel.length === 1 ? 'pessoa' : 'pessoas'} receberiam o texto com
              buraco. Não existe caminho que envie assim: ou você os exclui, ou reescreve o texto
              sem a variável que falta.
            </Text>
            <Stack gap={0}>
              {previa.semVariavel.slice(0, 6).map((s) => (
                <Text key={s.contatoId} size="xs" c="dimmed">
                  {s.nome ?? s.telefoneMascarado ?? '—'} · falta{' '}
                  {(s.faltando ?? []).map((v) => `{${v}}`).join(' ')}
                </Text>
              ))}
              {previa.semVariavel.length > 6 && (
                <Text size="xs" c="dimmed">
                  e mais {previa.semVariavel.length - 6}.
                </Text>
              )}
            </Stack>
            <Button
              size="xs"
              variant={excluirSemVariavel ? 'filled' : 'default'}
              onClick={() => aoAlternarExclusao(!excluirSemVariavel)}
            >
              {excluirSemVariavel
                ? `Excluindo essas ${previa.semVariavel.length} — clique para desfazer`
                : `Excluir essas ${previa.semVariavel.length} do envio`}
            </Button>
          </Stack>
        </Alert>
      )}

      {previa.exemplo && (
        <Paper withBorder p="sm" radius="md">
          <Stack gap={4}>
            <Text size="xs" c="dimmed" tt="uppercase" fw={600} style={{ letterSpacing: '0.04em' }}>
              Prévia — como {previa.exemplo.nome ?? 'o primeiro da lista'} vai receber
            </Text>
            {/* Texto literal, com as quebras de linha preservadas: é o que
                vai sair, não uma aproximação. */}
            <Text size="sm" style={{ whiteSpace: 'pre-wrap' }}>
              {previa.exemplo.texto}
            </Text>
          </Stack>
        </Paper>
      )}
    </Stack>
  );
}

// ---------------------------------------------------------------------------

const COR_DESTINATARIO: Record<EstadoDestinatario, string> = {
  pendente: 'gray',
  enviando: 'blue',
  enviado: 'teal',
  entregue: 'green',
  lido: 'green',
  falhou: 'red',
  adiado: 'yellow',
  pulado: 'gray',
};

/**
 * Detalhe do envio: as ações em curso e a tabela de destinatários.
 *
 * A TABELA É A RAZÃO DESTA TELA EXISTIR. Durante o disparo ela mostra onde a
 * lista está; depois, ela é o registro de quem recebeu e de por que os outros
 * não — com o texto que de fato saiu para cada um, que é o que permite
 * verificar uma reclamação meses depois.
 */
function DetalheDoEnvio({
  envioId,
  capacidades,
  aoFechar,
  aoMudar,
}: {
  envioId: string;
  capacidades: Capacidades;
  aoFechar: () => void;
  aoMudar: () => void;
}): JSX.Element {
  const [envio, setEnvio] = useState<EnvioDetalhe | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [ocupado, setOcupado] = useState(false);
  const [telefoneTeste, setTelefoneTeste] = useState('');
  const [filtro, setFiltro] = useState<string>('');
  const [confirmando, setConfirmando] = useState(false);
  const [redefinindo, setRedefinindo] = useState(false);

  const carregar = useCallback(async () => {
    try {
      setEnvio(await api.get<EnvioDetalhe>(`/admin/envios/${envioId}`));
      setErro(null);
    } catch (e) {
      setErro(explicarEnvio(e, 'falha ao carregar o envio'));
    }
  }, [envioId]);

  useEffect(() => {
    void carregar();
  }, [carregar]);

  // Em curso, a tela se atualiza sozinha: é onde se acompanha o disparo.
  useEffect(() => {
    if (envio?.estado !== 'enviando') return;
    const timer = setInterval(() => void carregar(), 10_000);
    return () => clearInterval(timer);
  }, [envio?.estado, carregar]);

  async function agir(rota: string, mensagem: string): Promise<void> {
    setOcupado(true);
    try {
      await api.post(`/admin/envios/${envioId}/${rota}`);
      notifications.show({ message: mensagem, color: 'green' });
      await carregar();
      aoMudar();
    } catch (e) {
      setErro(explicarEnvio(e, `falha ao ${rota}`));
    } finally {
      setOcupado(false);
    }
  }

  async function mandarTeste(): Promise<void> {
    setOcupado(true);
    try {
      await api.post(`/admin/envios/${envioId}/teste`, { telefone: telefoneTeste });
      notifications.show({
        title: 'Teste enfileirado',
        message:
          'A mensagem sai em instantes. Confira no celular e recarregue esta tela: o botão de iniciar destrava quando o teste sair.',
        color: 'green',
      });
      // Dá tempo de o worker gravar `testeEm` antes de a tela reler.
      setTimeout(() => void carregar(), 3_000);
    } catch (e) {
      setErro(explicarEnvio(e, 'falha ao enfileirar o teste'));
    } finally {
      setOcupado(false);
    }
  }

  async function enviarImagem(arquivo: File): Promise<void> {
    setOcupado(true);
    try {
      const base64 = await new Promise<string>((resolver, rejeitar) => {
        const leitor = new FileReader();
        leitor.onload = () => resolver(String(leitor.result));
        leitor.onerror = () => rejeitar(new Error('falha ao ler o arquivo'));
        leitor.readAsDataURL(arquivo);
      });
      await api.put(`/admin/envios/${envioId}/imagem`, {
        base64,
        mime: arquivo.type,
        nome: arquivo.name,
      });
      notifications.show({ message: 'Imagem anexada. O teste precisa ser refeito.', color: 'green' });
      await carregar();
    } catch (e) {
      setErro(explicarEnvio(e, 'falha ao anexar a imagem'));
    } finally {
      setOcupado(false);
    }
  }

  const visiveis = (envio?.destinatarios ?? []).filter((d) => !filtro || d.estado === filtro);

  return (
    <Modal opened onClose={aoFechar} title={envio?.nome ?? 'Envio'} size="xl" centered>
      {envio && confirmando && (
        <ConfirmarDisparo
          envio={envio}
          aoFechar={() => setConfirmando(false)}
          aoConfirmar={async () => {
            setConfirmando(false);
            await agir('iniciar', 'Envio iniciado.');
          }}
        />
      )}

      {envio && redefinindo && (
        <RedefinirDestinatarios
          envioId={envio.id}
          totalAtual={envio.contagens.total}
          aoFechar={() => setRedefinindo(false)}
          aoTrocar={() => {
            setRedefinindo(false);
            void carregar();
            aoMudar();
          }}
        />
      )}

      {erro && (
        <Alert color="red" variant="light" role="alert" mb="sm">
          <Text size="sm">{erro}</Text>
        </Alert>
      )}

      {!envio && (
        <Group gap="xs">
          <Loader size="sm" />
          <Text size="sm" c="dimmed">
            Carregando…
          </Text>
        </Group>
      )}

      {envio && (
        <Stack gap="md">
          <Group gap="xs">
            <Badge variant="light" color={COR_ESTADO_ENVIO[envio.estado]}>
              {ROTULO_ESTADO_ENVIO[envio.estado]}
            </Badge>
            <Badge variant="light">{envio.tipo === 'campanha' ? 'campanha' : 'aviso'}</Badge>
            <Text size="xs" c="dimmed">
              sai de {envio.instancia}
              {envio.numeroCompartilhado ? ' (o mesmo do atendimento)' : ' (instância dedicada)'}
            </Text>
          </Group>

          <FaixaDeNumeros>
            <Numero rotulo="na lista" valor={envio.contagens.total} />
            <Numero
              rotulo="enviados"
              valor={envio.contagens.enviados}
              nota={`${envio.contagens.entregues} entregues · ${envio.contagens.lidos} lidos`}
            />
            <Numero
              rotulo="falhas"
              valor={envio.contagens.falhas}
              {...(envio.contagens.falhas > 0 ? { cor: 'red' } : {})}
            />
            <Numero
              rotulo="não recebem"
              valor={envio.contagens.pulados + envio.contagens.adiados}
              nota={envio.contagens.adiados > 0 ? `${envio.contagens.adiados} adiados` : null}
            />
          </FaixaDeNumeros>

          <Paper withBorder p="sm" radius="md">
            <Stack gap={4}>
              <Text size="xs" c="dimmed" tt="uppercase" fw={600} style={{ letterSpacing: '0.04em' }}>
                Texto {envio.tipo === 'campanha' ? '(o rodapé é acrescentado no envio)' : ''}
              </Text>
              <Text size="sm" style={{ whiteSpace: 'pre-wrap' }}>
                {envio.texto}
              </Text>
              {envio.imagemCaminho && (
                <Text size="xs" c="dimmed">
                  Imagem anexada: {envio.imagemCaminho} ({envio.imagemMime})
                </Text>
              )}
            </Stack>
          </Paper>

          {/* ----------------------------------------------------------------
              Ações. O teste vem ANTES de iniciar na ordem da tela porque é
              a ordem obrigatória do fluxo.
          ---------------------------------------------------------------- */}
          {capacidades.disparar && (
            <Paper withBorder p="sm" radius="md">
              <Stack gap="sm">
                {envio.estado === 'rascunho' && (
                  <Group align="flex-end" gap="xs" wrap="wrap">
                    <TextInput
                      label="Enviar teste para"
                      description="Um celular seu. Obrigatório antes de iniciar."
                      placeholder="18999998888"
                      value={telefoneTeste}
                      onChange={(e) => setTelefoneTeste(e.currentTarget.value)}
                      size="xs"
                      w={220}
                    />
                    <Button
                      size="xs"
                      variant="default"
                      loading={ocupado}
                      disabled={!telefoneTeste.trim()}
                      onClick={() => void mandarTeste()}
                    >
                      Enviar teste
                    </Button>
                    <AnexarImagem ocupado={ocupado} aoEscolher={(f) => void enviarImagem(f)} />
                    <Button size="xs" variant="default" onClick={() => setRedefinindo(true)}>
                      Redefinir destinatários
                    </Button>
                  </Group>
                )}

                {envio.testeEm && (
                  <Text size="xs" c="dimmed">
                    Teste enviado em {data(envio.testeEm)}
                    {envio.testePara ? ` para ${envio.testePara}` : ''}.
                  </Text>
                )}

                <Group gap="xs">
                  {(envio.estado === 'rascunho' || envio.estado === 'agendado') && (
                    <Tooltip
                      label="Mande um envio de teste antes de iniciar"
                      disabled={envio.testeEm !== null}
                    >
                      <Button
                        size="xs"
                        loading={ocupado}
                        disabled={!podeIniciar(envio)}
                        // Abre a CONFIRMAÇÃO, não dispara. É a última tela
                        // antes de a mensagem sair para centenas de pessoas.
                        onClick={() => setConfirmando(true)}
                      >
                        Iniciar
                      </Button>
                    </Tooltip>
                  )}
                  {envio.estado === 'enviando' && (
                    <Button
                      size="xs"
                      variant="default"
                      loading={ocupado}
                      onClick={() => void agir('pausar', 'Envio pausado.')}
                    >
                      Pausar
                    </Button>
                  )}
                  {envio.estado === 'pausado' && (
                    <Button
                      size="xs"
                      loading={ocupado}
                      onClick={() => void agir('retomar', 'Envio retomado.')}
                    >
                      Retomar
                    </Button>
                  )}
                  {envio.estado !== 'concluido' && envio.estado !== 'cancelado' && (
                    <Button
                      size="xs"
                      color="red"
                      variant="light"
                      loading={ocupado}
                      onClick={() => void agir('cancelar', 'Envio cancelado.')}
                    >
                      Cancelar
                    </Button>
                  )}
                </Group>

                {envio.estado === 'pausado' && envio.pausaMotivo === 'disparo_desligado' && (
                  <Alert color="orange" variant="light" p="xs">
                    <Text size="xs">
                      Pausado porque o <strong>disparo em massa foi desligado</strong> na
                      configuração — não por alguém ter clicado em pausar. Retomar só funciona
                      depois de ligar a chave geral.
                    </Text>
                  </Alert>
                )}

                {envio.estado === 'pausado' && (
                  <Text size="xs" c="dimmed">
                    Ao retomar, quem foi <strong>adiado</strong> por estar em atendimento volta
                    para a fila — e o prazo de espera dele começa de novo daqui, porque durante a
                    pausa ninguém foi tentado.
                  </Text>
                )}
              </Stack>
            </Paper>
          )}

          <Divider label="Destinatários" labelPosition="left" />

          <BarraDeFiltros>
            <SegmentedControl
              size="xs"
              value={filtro}
              onChange={setFiltro}
              data={[
                { value: '', label: 'Todos' },
                { value: 'pendente', label: 'Na fila' },
                { value: 'enviado', label: 'Enviados' },
                { value: 'entregue', label: 'Entregues' },
                { value: 'lido', label: 'Lidos' },
                { value: 'falhou', label: 'Falhas' },
                { value: 'adiado', label: 'Adiados' },
                { value: 'pulado', label: 'Não recebem' },
              ]}
            />
            <Button
              size="xs"
              variant="default"
              component="a"
              href={`/admin/envios/${envioId}/csv`}
            >
              Exportar CSV
            </Button>
          </BarraDeFiltros>

          <ScrollArea.Autosize mah="40vh">
            <Table highlightOnHover verticalSpacing={4} fz="sm" stickyHeader>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Nome</Table.Th>
                  <Table.Th>Telefone</Table.Th>
                  <Table.Th>Estado</Table.Th>
                  <Table.Th>Motivo</Table.Th>
                  <Table.Th>Enviado</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {visiveis.map((d) => (
                  <Table.Tr key={d.contatoId}>
                    <Table.Td>
                      <Text size="sm">{d.nome ?? '—'}</Text>
                    </Table.Td>
                    <Table.Td>
                      <Text size="xs" ff="monospace" c="dimmed">
                        {d.telefone ?? '—'}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Badge size="xs" variant="light" color={COR_DESTINATARIO[d.estado]}>
                        {ROTULO_DESTINATARIO[d.estado]}
                      </Badge>
                    </Table.Td>
                    <Table.Td>
                      <Text size="xs" c="dimmed">
                        {explicarMotivoDestinatario(d.motivo) ??
                          (d.tentativas > 1 ? `${d.tentativas} tentativas` : '—')}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Text size="xs" c="dimmed">
                        {data(d.enviadoEm)}
                      </Text>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </ScrollArea.Autosize>

          {visiveis.length === 0 && (
            <Text size="sm" c="dimmed">
              Nenhum destinatário neste filtro.
            </Text>
          )}
        </Stack>
      )}
    </Modal>
  );
}

/**
 * Confirmação do disparo: a última tela antes de a mensagem sair.
 *
 * Existe por duas razões. A primeira é o modo sombra: `BOT_MODE=shadow` cala o
 * bot no atendimento e NÃO tem efeito aqui, então quem está em sombra precisa
 * ler isso no momento do clique, não só no topo da página que ele rolou cinco
 * minutos atrás. A segunda é que os números do ambiente mudam ao longo do dia —
 * o teto consumido e a janela aberta são relidos AQUI, e não herdados da
 * listagem, para a confirmação não repetir um estado de dez minutos antes.
 */
function ConfirmarDisparo({
  envio,
  aoFechar,
  aoConfirmar,
}: {
  envio: EnvioDetalhe;
  aoFechar: () => void;
  aoConfirmar: () => Promise<void>;
}): JSX.Element {
  const [contexto, setContexto] = useState<ContextoDeEnvios | null>(null);
  const [indo, setIndo] = useState(false);

  useEffect(() => {
    void api
      .get<ContextoDeEnvios>('/admin/envios/contexto')
      .then(setContexto)
      .catch(() => undefined);
  }, []);

  const pendentes = envio.contagens.pendentes;
  const cabeHoje = contexto === null || pendentes <= contexto.teto.restante;

  return (
    <Modal opened onClose={aoFechar} title="Confirmar disparo" centered zIndex={300}>
      <Stack gap="sm">
        {contexto && <AvisoDeSombra modoDoBot={contexto.modoDoBot} destaque />}

        <Text size="sm">
          Vai sair para{' '}
          <Text span fw={700}>
            {pendentes} {pendentes === 1 ? 'pessoa' : 'pessoas'}
          </Text>
          , a partir do número <Code>{envio.instancia}</Code>
          {envio.numeroCompartilhado ? ' — o mesmo do atendimento' : ''}.
        </Text>

        {envio.tipo === 'campanha' && contexto && (
          <Text size="sm" c="dimmed">
            Como é campanha, cada mensagem leva o rodapé “{contexto.rodapeCampanha}”.
          </Text>
        )}

        {contexto && (
          <Paper withBorder p="xs" radius="md">
            <Stack gap={4}>
              <Text size="xs" c="dimmed">
                Hoje já saíram {contexto.teto.usadasHoje} de {contexto.teto.teto}:{' '}
                {cabeHoje
                  ? `esta lista cabe no que resta (${contexto.teto.restante}).`
                  : `esta lista passa do teto — ela para ao bater ${contexto.teto.teto} e continua amanhã, do ponto em que estiver.`}
              </Text>
              <Text size="xs" c="dimmed">
                {contexto.janelaFechadaAte
                  ? `A janela está fechada: nada sai até ${contexto.janelaFechadaAte}.`
                  : `A janela está aberta (${contexto.limites.janelaInicio}h–${contexto.limites.janelaFim}h). Com pausa de ${contexto.limites.pausaMinSeg}–${contexto.limites.pausaMaxSeg}s, ${pendentes} mensagens levam cerca de ${Math.max(
                      1,
                      Math.round(
                        (pendentes *
                          ((contexto.limites.pausaMinSeg + contexto.limites.pausaMaxSeg) / 2)) /
                          60,
                      ),
                    )} min.`}
              </Text>
            </Stack>
          </Paper>
        )}

        <Group justify="flex-end">
          <Button variant="default" onClick={aoFechar}>
            Voltar
          </Button>
          <Button
            loading={indo}
            onClick={() => {
              setIndo(true);
              void aoConfirmar().finally(() => setIndo(false));
            }}
          >
            Disparar agora
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

/**
 * Redefinir QUEM recebe, num rascunho.
 *
 * SUBSTITUI a lista inteira, e a tela diz isso com palavras: o envio não guarda
 * os critérios (a lista é materializada na criação), então não há o que mesclar
 * nem o que pré-preencher. A alternativa — guardar os critérios só para
 * pré-popular este formulário — introduziria duas fontes para a mesma
 * informação, e a divergência entre elas seria invisível.
 *
 * Trocar os critérios ZERA O TESTE no servidor, e a tela avisa antes: quem
 * testou com uma turma e troca para outra não testou nada do que vai sair.
 */
function RedefinirDestinatarios({
  envioId,
  totalAtual,
  aoFechar,
  aoTrocar,
}: {
  envioId: string;
  totalAtual: number;
  aoFechar: () => void;
  aoTrocar: () => void;
}): JSX.Element {
  const [tipos, setTipos] = useState<string[]>([]);
  const [tags, setTags] = useState<string[]>([]);
  const [opcoesDeTipo, setOpcoesDeTipo] = useState<{ value: string; label: string }[]>([]);
  const [opcoesDeTag, setOpcoesDeTag] = useState<{ value: string; label: string }[]>([]);
  const [excluirSemVariavel, setExcluirSemVariavel] = useState(false);
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  useEffect(() => {
    void api
      .get<ListaMarcaveis>('/admin/contatos-marcaveis?limite=1')
      .then((r) =>
        setOpcoesDeTipo(r.tipos.map((t) => ({ value: t.tipo, label: `${t.tipo} (${t.total})` }))),
      )
      .catch(() => undefined);
    void api
      .get<{ itens: TagNaLista[] }>('/admin/tags')
      .then((r) =>
        setOpcoesDeTag(
          r.itens.map((t) => ({ value: t.id, label: `${t.nome} — ${t.alcancaveis} receberiam` })),
        ),
      )
      .catch(() => undefined);
  }, []);

  async function salvar(): Promise<void> {
    setSalvando(true);
    try {
      const r = await api.patch<ResultadoEdicaoDeEnvio>(`/admin/envios/${envioId}`, {
        criterios: { tipos, tags },
        ...(excluirSemVariavel ? { excluirSemVariavel: true } : {}),
      });
      notifications.show({
        title: 'Destinatários trocados',
        message: `Agora são ${r.total ?? 0}. O envio de teste caiu: mande outro antes de iniciar.`,
        color: 'green',
      });
      aoTrocar();
    } catch (e) {
      setErro(explicarEnvio(e, 'falha ao redefinir os destinatários'));
    } finally {
      setSalvando(false);
    }
  }

  const temCriterio = tipos.length > 0 || tags.length > 0;

  return (
    <Modal opened onClose={aoFechar} title="Redefinir destinatários" size="lg" centered zIndex={300}>
      <Stack gap="sm">
        <Alert color="yellow" variant="light">
          <Text size="sm">
            Isto <strong>substitui</strong> a lista inteira (hoje são {totalAtual}) e{' '}
            <strong>invalida o envio de teste</strong> — quem testou com uma turma e troca para
            outra não testou o que vai sair. Os critérios de antes não ficam guardados, então
            escolha tudo de novo.
          </Text>
        </Alert>

        <Group grow align="flex-start">
          <MultiSelect
            label="Tipo de pessoa"
            description="Vale qualquer um dos escolhidos."
            data={opcoesDeTipo}
            value={tipos}
            onChange={setTipos}
            searchable
            clearable
          />
          <MultiSelect
            label="Tags"
            description="Vale qualquer uma das escolhidas."
            data={opcoesDeTag}
            value={tags}
            onChange={setTags}
            searchable
            clearable
          />
        </Group>

        {tipos.length > 0 && tags.length > 0 && (
          <Text size="xs" c="dimmed">
            Com os dois preenchidos, a lista é a <strong>interseção</strong>.
          </Text>
        )}

        {erro && (
          <Alert color="red" variant="light" role="alert">
            <Stack gap="xs" align="flex-start">
              <Text size="sm">{erro}</Text>
              {/* A recusa por variável vazia é a única com saída aqui: a
                  decisão de excluir essas pessoas é do usuário. */}
              <Button
                size="xs"
                variant={excluirSemVariavel ? 'filled' : 'default'}
                onClick={() => setExcluirSemVariavel(!excluirSemVariavel)}
              >
                {excluirSemVariavel
                  ? 'Vou excluir quem ficaria com buraco — clique para desfazer'
                  : 'Excluir quem ficaria com buraco no texto'}
              </Button>
            </Stack>
          </Alert>
        )}

        <Group justify="flex-end">
          <Button variant="default" onClick={aoFechar}>
            Cancelar
          </Button>
          <Button loading={salvando} disabled={!temCriterio} onClick={() => void salvar()}>
            Trocar a lista
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

/**
 * Seleção de imagem.
 *
 * `input` nativo escondido em vez do `FileButton` do Mantine: o que esta tela
 * precisa é de uma linha, e o componente traria um ciclo de estado a mais sem
 * mudar nada do que o usuário vê.
 */
function AnexarImagem({
  ocupado,
  aoEscolher,
}: {
  ocupado: boolean;
  aoEscolher: (arquivo: File) => void;
}): JSX.Element {
  const ref = useRef<HTMLInputElement>(null);
  return (
    <>
      <input
        ref={ref}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        style={{ display: 'none' }}
        onChange={(e) => {
          const arquivo = e.currentTarget.files?.[0];
          if (arquivo) aoEscolher(arquivo);
          // Limpa para escolher o MESMO arquivo de novo depois de corrigi-lo.
          e.currentTarget.value = '';
        }}
      />
      <Button size="xs" variant="default" loading={ocupado} onClick={() => ref.current?.click()}>
        Anexar imagem
      </Button>
    </>
  );
}
